import {
  PurchaseComponentsProvider as SharedPurchaseComponentsProvider,
  type PurchaseComponentsProviderProps,
} from '@collect-purchase/shared-purchase-flow'
import { createContext, PropsWithChildren, useCallback, useContext, useMemo } from 'react'
import { useAuthContext, useAviosAuthEnabled } from '~/context/auth'
import { nextApiClient } from '~/services/clients/nextApiClient'
import { useFeatureFlag } from '~/context/featureFlag'

type PurchaseComponentsContextValue = {
  isConfigured: boolean
}

type PurchaseComponentsEnvironment = Partial<
  Record<
    | 'NEXT_PUBLIC_PURCHASE_API_URL'
    | 'NEXT_PUBLIC_PURCHASE_CONTENTFUL_ACCESS_TOKEN'
    | 'NEXT_PUBLIC_PURCHASE_CONTENTFUL_ENVIRONMENT'
    | 'NEXT_PUBLIC_PURCHASE_CONTENTFUL_SPACE_ID',
    string
  >
>

export const getPurchaseComponentsConfig = (
  environment: PurchaseComponentsEnvironment
): Omit<PurchaseComponentsProviderProps, 'children' | 'getAccessToken'> | null => {
  const apiUrl = environment.NEXT_PUBLIC_PURCHASE_API_URL?.trim()
  const accessToken = environment.NEXT_PUBLIC_PURCHASE_CONTENTFUL_ACCESS_TOKEN?.trim()
  const contentfulEnvironment = environment.NEXT_PUBLIC_PURCHASE_CONTENTFUL_ENVIRONMENT?.trim()
  const spaceId = environment.NEXT_PUBLIC_PURCHASE_CONTENTFUL_SPACE_ID?.trim()

  if (!apiUrl || !accessToken || !contentfulEnvironment) {
    return null
  }

  return {
    apiUrl,
    contentful: {
      accessToken,
      environment: contentfulEnvironment,
      ...(spaceId ? { spaceId } : {}),
    },
  }
}

const PurchaseComponentsContext = createContext<PurchaseComponentsContextValue>({
  isConfigured: false,
})

const getUserAccessToken = (user: ReturnType<typeof useAuthContext>['user']): string | null => {
  if (!user) {
    return null
  }

  if ('accessToken' in user && typeof user.accessToken === 'string') {
    return user.accessToken
  }

  if ('idToken' in user && typeof user.idToken === 'string') {
    return user.idToken
  }

  return null
}

const getAuth0AccessToken = async (): Promise<string | null> => {
  const { data } = await nextApiClient.get<{ accessToken: string | null }>('/auth/access-token')

  return data.accessToken
}

export const PurchaseComponentsProvider = ({ children }: PropsWithChildren) => {
  const purchaseWidgetEnabled = useFeatureFlag('show-purchase-balance-booster-widget')
  const purchaseComponentsConfig = useMemo(
    () =>
      getPurchaseComponentsConfig({
        NEXT_PUBLIC_PURCHASE_API_URL: process.env.NEXT_PUBLIC_PURCHASE_API_URL,
        NEXT_PUBLIC_PURCHASE_CONTENTFUL_ACCESS_TOKEN:
          process.env.NEXT_PUBLIC_PURCHASE_CONTENTFUL_ACCESS_TOKEN,
        NEXT_PUBLIC_PURCHASE_CONTENTFUL_ENVIRONMENT:
          process.env.NEXT_PUBLIC_PURCHASE_CONTENTFUL_ENVIRONMENT,
        NEXT_PUBLIC_PURCHASE_CONTENTFUL_SPACE_ID:
          process.env.NEXT_PUBLIC_PURCHASE_CONTENTFUL_SPACE_ID,
      }),
    []
  )
  const { user } = useAuthContext()
  const aviosAuthEnabled = useAviosAuthEnabled()
  const centralAviosAccessToken = getUserAccessToken(user)
  const getAccessToken = useCallback(
    async () => (aviosAuthEnabled ? centralAviosAccessToken : getAuth0AccessToken()),
    [aviosAuthEnabled, centralAviosAccessToken]
  )

  const contextValue = useMemo(
    () => ({ isConfigured: purchaseComponentsConfig !== null }),
    [purchaseComponentsConfig]
  )

  const content =
    purchaseWidgetEnabled && purchaseComponentsConfig ? (
      <SharedPurchaseComponentsProvider
        {...purchaseComponentsConfig}
        getAccessToken={getAccessToken}
      >
        {children}
      </SharedPurchaseComponentsProvider>
    ) : (
      children
    )

  return (
    <PurchaseComponentsContext.Provider value={contextValue}>
      {content}
    </PurchaseComponentsContext.Provider>
  )
}

export const usePurchaseComponents = (): PurchaseComponentsContextValue =>
  useContext(PurchaseComponentsContext)
